import * as React from "react";
import { useReducer, useCallback, useEffect, useMemo } from "react";
import { loadRecaptchaScript, waitForRecaptchaReady, isRecaptchaLoaded } from "../../utils/utils/recaptcha-loader.js";
import { RecaptchaContext } from "./RecaptchaContext.js";
const initialState = {
  loadingState: "idle",
  isReady: false,
  error: null,
  config: null
};
function recaptchaReducer(state, action) {
  switch (action.type) {
    case "SET_LOADING_STATE":
      return {
        ...state,
        loadingState: action.payload,
        isReady: action.payload === "loaded",
        error: action.payload === "error" ? state.error : null
      };
    case "SET_ERROR":
      return {
        ...state,
        error: action.payload,
        loadingState: action.payload ? "error" : state.loadingState,
        isReady: false
      };
    case "SET_CONFIG":
      return {
        ...state,
        config: action.payload
      };
    case "RESET":
      return initialState;
    default:
      return state;
  }
}
function RecaptchaProvider({ config, children }) {
  const [state, dispatch] = useReducer(recaptchaReducer, initialState);
  const load = useCallback(async () => {
    if (!config || !config.siteKey) {
      dispatch({ type: "SET_ERROR", payload: "reCAPTCHA site key is required" });
      return;
    }
    try {
      dispatch({ type: "SET_LOADING_STATE", payload: "loading" });
      dispatch({ type: "SET_ERROR", payload: null });
      await loadRecaptchaScript(config);
      await waitForRecaptchaReady();
      dispatch({ type: "SET_LOADING_STATE", payload: "loaded" });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to load reCAPTCHA";
      console.error("Failed to load reCAPTCHA script:", error);
      dispatch({ type: "SET_ERROR", payload: errorMessage });
      dispatch({ type: "SET_LOADING_STATE", payload: "error" });
    }
  }, [config]);
  const execute = useCallback(
    async (options) => {
      if (!state.isReady || !state.config) {
        throw new Error("reCAPTCHA is not ready. Make sure to load it first.");
      }
      if (!isRecaptchaLoaded()) {
        throw new Error("reCAPTCHA script is not loaded");
      }
      try {
        const token = await window.grecaptcha.enterprise.execute(state.config.siteKey, {
          action: options.action
        });
        return {
          token,
          action: options.action,
          timestamp: Date.now()
        };
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "Failed to execute reCAPTCHA";
        dispatch({ type: "SET_ERROR", payload: errorMessage });
        throw new Error(errorMessage);
      }
    },
    [state.isReady, state.config]
  );
  const reset = useCallback(() => {
    dispatch({ type: "RESET" });
  }, []);
  useEffect(() => {
    if (!config) {
      return;
    }
    dispatch({ type: "SET_CONFIG", payload: config });
    if (config.autoLoad !== false) {
      load();
    }
  }, [config, load]);
  const contextValue = useMemo(
    () => ({
      ...state,
      execute,
      load,
      reset
    }),
    [state, execute, load, reset]
  );
  return /* @__PURE__ */ React.createElement(RecaptchaContext.Provider, { value: contextValue }, children);
}
export {
  RecaptchaProvider
};
//# sourceMappingURL=RecaptchaProvider.js.map
